import { useMemo } from 'react'
import { Controller } from 'react-hook-form'
import { Box, FormControl, FormErrorMessage, FormHelperText, FormLabel, HStack } from '@chakra-ui/react'
import { components, CreatableSelect, OptionProps, PropsValue } from 'chakra-react-select'

import { useEventFormOptions } from '@hooks/useEventFormOptions'

import { MatchEventEditorCardExtraInfo, MatchEventEditorEventType, MatchEventEditorGoalExtraInfo } from '@common/types'

import { useMatchEditorFormContext } from '@contexts/MatchEditorFormContext'

import { type Option, playerSearchFilter } from '../utils'

type PlayerOptionProps = OptionProps<Option, false>

function PlayerOption(props: PlayerOptionProps) {
    return (
        <components.Option {...props}>
            <HStack justifyContent="space-between">
                <Box as="span">{props.data.label}</Box>
                <Box as="span" fontFamily="monospace">
                    [{props.data.value}]
                </Box>
            </HStack>
        </components.Option>
    )
}

export function PlayersSelectorGroup() {
    const { zodForm, createFakePlayer, isPlayersError, isCreatingPlayer, isFormDisabled, isPenaltyOrderRequired } =
        useMatchEditorFormContext()

    const { players, teams } = useEventFormOptions()

    const selectedEventType = zodForm.watch('type.value')
    const selectedExtraInfo = zodForm.watch('extra_info')
    const selectedTeam = zodForm.watch('team_id')

    const { player1Label, player2Label } = useMemo(() => {
        const EMPTY_LABELS = { player1Label: '', player2Label: '' }
        switch (Number(selectedEventType)) {
            case MatchEventEditorEventType.goal:
                return { player1Label: 'Goalscorer', player2Label: isPenaltyOrderRequired ? '' : 'Player to assist' }

            case MatchEventEditorEventType.yellow_card:

            case MatchEventEditorEventType.yellowred_card:

            case MatchEventEditorEventType.red_card: {
                const extraInfoTypesWithoutPlayer = [
                    MatchEventEditorCardExtraInfo.manager,
                    MatchEventEditorCardExtraInfo.staff,
                ].map(Number)

                let tempPlayer1Label = 'Player getting booked'

                if (selectedExtraInfo && extraInfoTypesWithoutPlayer.includes(Number(selectedExtraInfo))) {
                    tempPlayer1Label = ''
                }

                return {
                    player1Label: tempPlayer1Label,
                    player2Label: '',
                }
            }

            case MatchEventEditorEventType.substitution:
                return {
                    player1Label: 'Player out',
                    player2Label: 'Player in',
                }

            case MatchEventEditorEventType.shot_on_target:

            case MatchEventEditorEventType.shot_off_target:

            case MatchEventEditorEventType.shot_blocked:

            case MatchEventEditorEventType.penalty_missed:

            case MatchEventEditorEventType.penalty_shootout_event_deep_coverage: {
                return {
                    player1Label: 'Shooter',
                    player2Label: '',
                }
            }

            case MatchEventEditorEventType.video_assistant_referee:

            case MatchEventEditorEventType.video_assistant_referee_over: {
                return {
                    player1Label: 'Player involved',
                    player2Label: '',
                }
            }

            default:
                return EMPTY_LABELS
        }
    }, [selectedEventType, selectedExtraInfo, isPenaltyOrderRequired])

    const availablePlayers = useMemo(() => {
        if (!selectedTeam?.value) {
            return []
        }

        const isOwnGoal =
            Number(selectedEventType) === MatchEventEditorEventType.goal &&
            Number(selectedExtraInfo) === MatchEventEditorGoalExtraInfo.own_goal

        const unselectedTeamId = teams.find(team => team.value !== selectedTeam.value)?.value ?? ''

        const selectedTeamPlayers = players[String(selectedTeam.value)] ?? []
        const unselectedTeamPlayers = players[unselectedTeamId] ?? []

        return isOwnGoal ? unselectedTeamPlayers : selectedTeamPlayers
    }, [selectedTeam?.value, selectedEventType, selectedExtraInfo, teams, players])

    if (!player1Label) {
        return null
    }

    return (
        <HStack alignItems="flex-start" mb={8} w="100%">
            <Controller
                control={zodForm.control}
                name="player1_id"
                render={({ field: { onChange, onBlur, value, name, ref }, fieldState: { error } }) => (
                    <FormControl isInvalid={isPlayersError()} isDisabled={isFormDisabled}>
                        <FormLabel htmlFor={name}>{player1Label}</FormLabel>
                        <CreatableSelect
                            id={name}
                            components={{ Option: PlayerOption }}
                            filterOption={playerSearchFilter}
                            isSearchable
                            isClearable
                            name={name}
                            ref={ref}
                            onChange={newValue => onChange(newValue || { value: '', label: '' })}
                            onBlur={onBlur}
                            isLoading={isCreatingPlayer}
                            onCreateOption={newPlayerValue => createFakePlayer(newPlayerValue, name)}
                            value={value as unknown as PropsValue<Option>}
                            placeholder={`Select ${player1Label}`}
                            options={availablePlayers}
                        />
                        <FormHelperText>Note: You can search by player ID or manually enter the name</FormHelperText>
                        {isPlayersError() && <FormErrorMessage>Select either both players or none</FormErrorMessage>}
                    </FormControl>
                )}
            />
            {!!player2Label.length && (
                <Controller
                    control={zodForm.control}
                    name="player2_id"
                    render={({ field: { onChange, onBlur, value, name, ref }, fieldState: { error } }) => (
                        <FormControl isInvalid={isPlayersError()}>
                            <FormLabel htmlFor={name}>{player2Label}</FormLabel>
                            <CreatableSelect
                                id={name}
                                components={{ Option: PlayerOption }}
                                filterOption={playerSearchFilter}
                                isSearchable
                                isClearable
                                name={name}
                                ref={ref}
                                onChange={newValue => onChange(newValue || { value: '', label: '' })}
                                onBlur={onBlur}
                                isLoading={isCreatingPlayer}
                                onCreateOption={newPlayerValue => createFakePlayer(newPlayerValue, name)}
                                value={value as unknown as PropsValue<Option>}
                                placeholder={`Select ${player2Label}`}
                                options={availablePlayers}
                            />
                            <FormHelperText>
                                Note: You can search by player ID or manually enter the name
                            </FormHelperText>
                        </FormControl>
                    )}
                />
            )}
        </HStack>
    )
}
